// MenuRouting.js - Centralized menu scene routing with feature flag support
// Eliminates hardcoded menu scene names and ensures consistent routing

import { auth } from '../firebaseConfig.js';
import { hasSpecialAccess } from '../services/specialAccessService.js';
import { MobileDetectionService } from '../services/MobileDetectionService.js';

export default class MenuRouting {
  // Get the correct menu scene based on authentication status and feature flags
  static determineMenuScene(userOverride = null) {
    const user = userOverride || auth.currentUser;
    const specialAccess = hasSpecialAccess();
    const isEffectivelyLoggedIn = (user && user.emailVerified) || specialAccess;
    
    // Get feature flags
    const useUniversalLanding = localStorage.getItem('pptHeroUseUniversalLanding') === 'true';
    const useDOMMenus = localStorage.getItem('pptHeroUseDOMMenus') === 'true';
    const useDOMAuthMenu = localStorage.getItem('pptHeroUseDOMAuthMenu') === 'true';
    
    console.log('🧭 MenuRouting: Determining menu scene...', {
      userEmail: user?.email || 'none',
      emailVerified: user?.emailVerified || false,
      specialAccess,
      isEffectivelyLoggedIn,
      useUniversalLanding,
      useDOMMenus,
      useDOMAuthMenu
    });

    // CRITICAL FIX: Universal landing only for guest users
    // Authenticated users should go to executive dashboard regardless of universal landing flag
    
    // Authenticated users (verified email OR special access) - Go to executive dashboard
    if (isEffectivelyLoggedIn) {
      const baseScene = useDOMAuthMenu ? 'AuthenticatedMenuSceneDOM' : 'AuthenticatedMenuScene';
      
      // ROBUST: Always use enterprise mobile for authenticated mobile users
      const isMobile = MobileDetectionService.isMobile();
      if (isMobile) {
        return 'EnterpriseAuthenticatedMenuScene'; // Mobile always gets enterprise scene
      }
      
      return baseScene; // Desktop gets original scene
    }
    
    // Guest users - Use enhanced guest menu with mobile optimizations
    if (useUniversalLanding) {
      return 'MainMenuSceneUniversal';
    }
    
    // ARCHITECTURAL FIX: Mobile guests use GuestMenuSceneDOM with mobile enhancements
    // EnterpriseAuthenticatedMenuScene is only for authenticated users
    return useDOMMenus ? 'GuestMenuSceneDOM' : 'GuestMenuScene';
  }

  // Get the correct main/guest menu (when user is not authenticated)
  static determineGuestMenuScene() {
    const useUniversalLanding = localStorage.getItem('pptHeroUseUniversalLanding') === 'true';
    const useDOMMenus = localStorage.getItem('pptHeroUseDOMMenus') === 'true';
    
    if (useUniversalLanding) {
      return 'MainMenuSceneUniversal';
    }
    
    return useDOMMenus ? 'GuestMenuSceneDOM' : 'GuestMenuScene';
  }

  // Get the correct authenticated menu
  static determineAuthenticatedMenuScene() {
    const useDOMAuthMenu = localStorage.getItem('pptHeroUseDOMAuthMenu') === 'true';
    return useDOMAuthMenu ? 'AuthenticatedMenuSceneDOM' : 'AuthenticatedMenuScene';
  }

  // Route to appropriate menu based on current user status
  static async routeToMenu(sceneContext) {
    const targetScene = this.determineMenuScene();
    console.log('🧭 MenuRouting: Routing to menu scene:', targetScene);
    
    if (sceneContext && sceneContext.scene) {
      // Try SceneManager first for professional transitions
      if (window.sceneManager && window.sceneManager.switchToScene) {
        try {
          await window.sceneManager.switchToScene(targetScene);
          return;
        } catch (error) {
          console.warn('🧭 MenuRouting: SceneManager failed, using direct transition:', error);
        }
      }
      
      // Fallback to direct scene transition
      sceneContext.scene.start(targetScene);
    } else {
      console.error('🧭 MenuRouting: No valid scene context provided');
    }
  }

  // Route to specific menu type (for error recovery, etc.)
  static async routeToGuestMenu(sceneContext) {
    const targetScene = this.determineGuestMenuScene();
    console.log('🧭 MenuRouting: Routing to guest menu:', targetScene);
    
    if (window.sceneManager && window.sceneManager.switchToScene) {
      await window.sceneManager.switchToScene(targetScene);
    } else if (sceneContext && sceneContext.scene) {
      sceneContext.scene.start(targetScene);
    }
  }

  static async routeToAuthenticatedMenu(sceneContext) {
    const targetScene = this.determineAuthenticatedMenuScene();
    console.log('🧭 MenuRouting: Routing to authenticated menu:', targetScene);
    
    if (window.sceneManager && window.sceneManager.switchToScene) {
      await window.sceneManager.switchToScene(targetScene);
    } else if (sceneContext && sceneContext.scene) {
      sceneContext.scene.start(targetScene);
    }
  }

  // Check if a scene is a menu scene
  static isMenuScene(sceneKey) {
    const menuScenes = [
      'MainMenuSceneUniversal',
      'MainMenuSceneDOM',
      'MainMenuScene',
      'GuestMenuSceneDOM', 
      'GuestMenuScene',
      'AuthenticatedMenuSceneDOM',
      'AuthenticatedMenuScene'
    ];
    
    return menuScenes.includes(sceneKey);
  }

  // Get all menu scene variants for stopping during transitions
  static getAllMenuScenes() {
    return [
      'MainMenuSceneUniversal',
      'MainMenuSceneDOM', 
      'MainMenuScene',
      'GuestMenuSceneDOM',
      'GuestMenuScene', 
      'AuthenticatedMenuSceneDOM',
      'AuthenticatedMenuScene'
    ];
  }

  // Debug information for troubleshooting
  static getRoutingDebugInfo() {
    const user = auth.currentUser;
    const specialAccess = hasSpecialAccess();
    
    return {
      currentUser: {
        email: user?.email || 'none',
        emailVerified: user?.emailVerified || false,
        uid: user?.uid || 'none'
      },
      specialAccess: specialAccess,
      featureFlags: {
        useUniversalLanding: localStorage.getItem('pptHeroUseUniversalLanding') === 'true',
        useDOMMenus: localStorage.getItem('pptHeroUseDOMMenus') === 'true', 
        useDOMAuthMenu: localStorage.getItem('pptHeroUseDOMAuthMenu') === 'true'
      },
      recommendedScene: this.determineMenuScene(),
      allMenuScenes: this.getAllMenuScenes()
    };
  }
}

// Make globally available for debugging
window.MenuRouting = MenuRouting;
console.log('🧭 MenuRouting: Centralized routing service created');
